/**
 * /dashboard/posh/complaints — POSH complaints landing.
 *
 * Two surfaces, deliberately separated by the 2FA boundary:
 *  - "My complaints" — GET /posh/my-complaints (own, NOT 2FA-gated).
 *  - "ICC register"  — GET /posh/complaints (STRICT 2FA).
 *
 * Migration note (Phase 3f — 2026-06-01): the new + detail/manage
 * sub-routes are now ported; <a href> swapped for typed Link.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { useState } from "react";
import { Plus } from "lucide-react";
import { toApiError } from "@/lib/api";
import {
  is2FARequiredError,
  useComplaints,
  useMyComplaints,
} from "@/hooks/use-posh";
import { complainantLabel, fmtDate } from "@/lib/posh-format";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { TwoFactorRequired } from "@/components/posh/two-factor-required";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import type { ComplaintDTO } from "@/types/posh";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/complaints/")({
  component: ComplaintsRoute,
});

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-2 h-4 w-1/2" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function ComplaintRow({
  c,
  variant,
}: {
  c: ComplaintDTO;
  /** "own" → /dashboard/posh/complaints/$id; "manage" → that + /manage. */
  variant: "own" | "manage";
}) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const body = (
    <Card className="transition-colors group-hover:border-primary/40">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base">
              {c.complaint_number}
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Against {c.respondent_name} · Incident{" "}
              {fmtDate(c.incident_date)} · {complainantLabel(c)}
            </p>
          </div>
          <StatusBadge status={c.status} />
        </div>
      </CardHeader>
    </Card>
  );
  if (variant === "manage") {
    return (
      <Link
        to="/app/$tenantSlug/posh/complaints/$id/manage"
        params={{tenantSlug, id: String(c.id)}}
        className="group block"
      >
        {body}
      </Link>
    );
  }
  return (
    <Link
      to="/app/$tenantSlug/posh/complaints/$id"
      params={{tenantSlug, id: String(c.id)}}
      className="group block"
    >
      {body}
    </Link>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-12 text-center text-sm text-muted-foreground">
        {message}
      </CardContent>
    </Card>
  );
}

function MyComplaints() {
  const q = useMyComplaints();
  if (q.isLoading) return <ListSkeleton />;
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load your complaints</AlertTitle>
        <AlertDescription>
          {toApiError(q.error).message}
        </AlertDescription>
      </Alert>
    );
  }
  const items = q.data?.complaints ?? [];
  if (items.length === 0) {
    return (
      <EmptyState message='You have not filed any complaints. Use "File a complaint" above to raise one — you can track it here.' />
    );
  }
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <ComplaintRow
          key={c.id}
          c={c}
          variant="own"
        />
      ))}
    </div>
  );
}

function ICCRegister() {
  const q = useComplaints();

  if (q.isLoading) return <ListSkeleton />;

  if (q.isError && is2FARequiredError(q.error)) {
    return <TwoFactorRequired surface="the ICC complaint register" />;
  }
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load the complaint register</AlertTitle>
        <AlertDescription>
          {toApiError(q.error).message}
        </AlertDescription>
      </Alert>
    );
  }

  const items = q.data?.complaints ?? [];
  if (items.length === 0) {
    return (
      <EmptyState message="No complaints are visible to you in this tenant." />
    );
  }
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <ComplaintRow
          key={c.id}
          c={c}
          variant="manage"
        />
      ))}
    </div>
  );
}

function ComplaintsRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [tab, setTab] = useState("mine");
  return (
    <PoshModuleShell
      title="Complaints"
      description="File and track a POSH complaint. The committee register is protected by two-factor authentication."
      actions={
        <Button asChild className="gap-1.5">
          <Link to="/app/$tenantSlug/posh/complaints/new" params={{ tenantSlug }}>
            <Plus className="h-4 w-4" />
            File a complaint
          </Link>
        </Button>
      }
    >
      <PoshFeatureGate>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="mine">My complaints</TabsTrigger>
            <TabsTrigger value="icc">ICC register</TabsTrigger>
          </TabsList>
          <TabsContent value="mine" className="mt-4">
            <MyComplaints />
          </TabsContent>
          <TabsContent value="icc" className="mt-4">
            <ICCRegister />
          </TabsContent>
        </Tabs>
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
